import { useCallback, useEffect, useRef, useState } from 'react';

import type { WalletMfaManagement } from '@/features/wallet-mfa/use-wallet-mfa-management';
import {
  classifyWalletMfaError,
  isWalletMfaVerificationCancelled,
  WalletMfaErrorKind,
} from '@/features/wallet-mfa/wallet-mfa-errors';
import type { WalletTotpEnrollmentMaterial } from '@/features/wallet-mfa/wallet-mfa-types';
import type { OtpInputHandle } from '@/shared/design-system';

type WalletTotpDialogControllerOptions = {
  open: boolean;
  management: WalletMfaManagement;
  onClose: () => void;
};

export const useWalletTotpDialogController = ({
  open,
  management,
  onClose,
}: WalletTotpDialogControllerOptions) => {
  const [material, setMaterial] = useState<WalletTotpEnrollmentMaterial | null>(null);
  const [code, setCode] = useState('');
  const [keySaved, setKeySaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [copiedAnnouncement, setCopiedAnnouncement] = useState('');
  const startedRef = useRef(false);
  const otpRef = useRef<OtpInputHandle>(null);

  const reset = useCallback(() => {
    startedRef.current = false;
    setMaterial(null);
    setCode('');
    setKeySaved(false);
    setError(null);
    setPending(false);
    setCopiedAnnouncement('');
  }, []);

  const close = useCallback(() => {
    if (pending) return;
    reset();
    onClose();
  }, [onClose, pending, reset]);

  const prepareSetup = useCallback(() => {
    startedRef.current = true;
    setError(null);
    setPending(true);
    void management.startEnrollment().then(
      (nextMaterial) => {
        setMaterial(nextMaterial);
        setPending(false);
      },
      () => {
        setError('Authenticator setup could not be started. Please try again.');
        setPending(false);
      },
    );
  }, [management]);

  useEffect(() => {
    if (!open || startedRef.current) return;
    prepareSetup();
  }, [open, prepareSetup]);

  useEffect(() => {
    if (error && material) otpRef.current?.focusFirst();
  }, [error, material]);

  const changeCode = useCallback(
    (nextCode: string) => {
      setCode(nextCode);
      setError(null);
      if (nextCode.length !== 6 || !keySaved) return;

      setPending(true);
      void management.submitEnrollment(nextCode).then(
        () => {
          reset();
          onClose();
        },
        (submissionError) => {
          if (isWalletMfaVerificationCancelled(submissionError)) {
            reset();
            onClose();
            return;
          }
          const classified = classifyWalletMfaError(submissionError);
          setCode('');
          setError(
            classified.kind === WalletMfaErrorKind.InvalidCode
              ? classified.message
              : 'Authenticator setup could not be completed. Please try again.',
          );
          setPending(false);
        },
      );
    },
    [keySaved, management, onClose, reset],
  );

  const copySecret = useCallback(async () => {
    if (!material || !navigator.clipboard?.writeText) return;
    setCopiedAnnouncement('');
    try {
      await navigator.clipboard.writeText(material.sharedSecret);
      setCopiedAnnouncement('Key copied');
    } catch {
      setCopiedAnnouncement('');
    }
  }, [material]);

  return {
    material,
    code,
    keySaved,
    setKeySaved,
    error,
    pending,
    copiedAnnouncement,
    otpRef,
    close,
    prepareSetup,
    changeCode,
    copySecret,
  };
};
